import type { InfiniteData, QueryClient } from '@tanstack/react-query';

import {
  type MyQuizCursorPage,
  type MyQuizCursorVariables,
  myQuizListKey,
  useFetchMyQuizesQuery,
} from '../hooks/useFetchMyQuizes';

// 내 퀴즈 목록(`/users/myquiz/cursor`)은 검색 인덱스에서 `folderId` 를 읽는다. 퀴즈를 옮기거나 복사한
// 직후에는 인덱스가 원본을 따라오기 전(보통 1초 안팎)의 결과가 온다.
//
// - 결과를 알고 있는 변경(이동)은 캐시를 직접 고친다.
// - 모르는 변경(복사로 생긴 새 퀴즈, 폴더 삭제 등)은 시간을 두고 한 번 더 받는다.

/**
 * 검색 인덱스가 원본을 따라오기까지 기다리는 여유. 관측값(1초 안팎)에 여유를 더했다.
 * 내 퀴즈 목록과 내 폴더 목록(`folderListCache`)이 같은 인덱스를 읽어 함께 쓴다.
 */
export const SEARCH_INDEX_LAG_MS = 1_500;

/** 커서 목록만 고르는 키. `myQuizListKey` 와 달리 이 쿼리 하나만 가리키므로 쿼리 정의에서 파생한다. */
const MY_QUIZ_CURSOR_KEY = useFetchMyQuizesQuery.getKey();

type MyQuizCursorData = InfiniteData<MyQuizCursorPage, string | undefined>;

/** 지금 한 번, 인덱스가 따라온 뒤 한 번 더 받는다. */
export const invalidateMyQuizList = (queryClient: QueryClient) => {
  void queryClient.invalidateQueries({ queryKey: myQuizListKey });
  setTimeout(() => {
    void queryClient.invalidateQueries({ queryKey: myQuizListKey });
  }, SEARCH_INDEX_LAG_MS);
};

/**
 * 이동 결과를 내 퀴즈 목록 캐시에 바로 반영한다.
 *
 * - 폴더 밖 목록(`rootOnly`)에서 폴더로 옮긴 퀴즈는 뺀다.
 * - 검색 목록은 카드의 폴더 표시만 바꾼다.
 * - 폴더 밖으로 꺼낸 퀴즈는 넣지 않는다(카드를 그릴 데이터가 없다). 이어지는 재조회가 채운다.
 *
 * 진행 중인 조회를 먼저 취소한다 — 인덱스가 따라오기 전 결과가 고친 캐시를 덮어쓰지 않게 한다.
 */
export const applyQuizMoveToMyQuizList = async (
  queryClient: QueryClient,
  quizId: string,
  destination: { folderId: string; folderName: string } | undefined,
) => {
  await queryClient.cancelQueries({ queryKey: MY_QUIZ_CURSOR_KEY });

  for (const [key, data] of queryClient.getQueriesData<MyQuizCursorData>({
    queryKey: MY_QUIZ_CURSOR_KEY,
  })) {
    if (!data) continue;
    // react-query-kit 의 키는 `[...queryKey, variables]` 다.
    const variables = key.at(-1) as MyQuizCursorVariables | undefined;
    const removeFromList = !!variables?.rootOnly && !!destination;
    const isInList = data.pages.some(page =>
      page.items.some(item => item.id === quizId),
    );
    if (!isInList) continue;

    queryClient.setQueryData<MyQuizCursorData>(key, {
      ...data,
      pages: data.pages.map(page => ({
        ...page,
        items: removeFromList
          ? page.items.filter(item => item.id !== quizId)
          : page.items.map(item =>
              item.id === quizId
                ? {
                    ...item,
                    folderId: destination?.folderId,
                    folderName: destination?.folderName,
                  }
                : item,
            ),
        totalCount:
          removeFromList && page.totalCount !== undefined
            ? page.totalCount - 1
            : page.totalCount,
      })),
    });
  }
};
